import React from 'react';
import DOMPurify from 'dompurify';
import { Destination } from '../../types';
import { MapPin, Calendar, Eye, Tag, Image as ImageIcon, HelpCircle, Globe, Map, Edit, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';

interface ViewDestinationPageProps {
  destination: Destination;
  onEdit: () => void;
  onBack: () => void;
}

export const ViewDestinationPage: React.FC<ViewDestinationPageProps> = ({
  destination,
  onEdit,
  onBack,
}) => {
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
  };

  const renderHtmlContent = (html?: string) => {
    if (!html) return null;
    const sanitized = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    return <div dangerouslySetInnerHTML={{ __html: sanitized }} className="prose dark:prose-invert max-w-none" />;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              Back to Destinations
            </button>
            <button
              onClick={onEdit}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Edit className="h-5 w-5 mr-2" />
              Edit Destination
            </button>
          </div>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {destination.title}
              </h1>
              {destination.slug && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Slug: <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{destination.slug}</code>
                </p>
              )}
              <div className="flex items-center space-x-4 mt-2">
                {destination.published ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm font-medium">
                    Published
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-full text-sm font-medium">
                    Draft
                  </span>
                )}
                {destination.views !== undefined && (
                  <span className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Eye className="h-4 w-4 mr-1" />
                    {destination.views} views
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {destination.summary && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {destination.summary}
            </p>
          </div>
        )}

        {/* Description */}
        {destination.description && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Description</h2>
            <div className="prose dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
              {renderHtmlContent(destination.description)}
            </div>
          </div>
        )}

        {/* Location */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <MapPin className="h-5 w-5 mr-2" />
            Location
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {destination.country && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Country</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{destination.country}</p>
              </div>
            )}
            {destination.state && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">State</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{destination.state}</p>
              </div>
            )}
            {destination.region && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Region</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{destination.region}</p>
              </div>
            )}
          </div>
          {destination.geolocation && destination.geolocation.coordinates && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                <Map className="h-4 w-4 mr-1" />
                Coordinates
              </p>
              <p className="text-gray-900 dark:text-white">
                Longitude: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{destination.geolocation.coordinates[0]}</code>
                {' '} • {' '}
                Latitude: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{destination.geolocation.coordinates[1]}</code>
              </p>
            </div>
          )}
        </div>

        {/* Why Join */}
        {destination.whyJoin && destination.whyJoin.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Why Join</h2>
            <ul className="space-y-2">
              {destination.whyJoin.map((item, index) => (
                <li key={index} className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Highlights */}
        {destination.highlights && destination.highlights.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {destination.highlights.map((highlight, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700 dark:text-gray-300">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        {destination.photos && destination.photos.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <ImageIcon className="h-5 w-5 mr-2" />
              Photos ({destination.photos.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {destination.photos.map((photo, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                  <div className="relative aspect-video bg-gray-200 dark:bg-gray-800">
                    <img
                      src={photo.url}
                      alt={photo.alt || `Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    {photo.isHero && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded">
                        Hero
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    {photo.alt && (
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{photo.alt}</p>
                    )}
                    {photo.caption && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{photo.caption}</p>
                    )}
                    {photo.credit && (
                      <p className="text-xs text-gray-500 dark:text-gray-500">Credit: {photo.credit}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {photo.type && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                          {photo.type}
                        </span>
                      )}
                      {photo.width && photo.height && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded">
                          {photo.width} × {photo.height}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQs */}
        {destination.faqs && destination.faqs.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <HelpCircle className="h-5 w-5 mr-2" />
              FAQs ({destination.faqs.length})
            </h2>
            <div className="space-y-4">
              {destination.faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Q{index + 1}: {faq.question}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 pl-4 border-l-4 border-blue-600">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {destination.tags && destination.tags.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Tag className="h-5 w-5 mr-2" />
              Tags
            </h2>
            <div className="flex flex-wrap gap-2">
              {destination.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* SEO / Meta */}
        {destination.meta && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Globe className="h-5 w-5 mr-2" />
              SEO / Meta Information
            </h2>
            <div className="space-y-4">
              {destination.meta.metaTitle && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Meta Title</p>
                  <p className="text-gray-900 dark:text-white">{destination.meta.metaTitle}</p>
                </div>
              )}
              {destination.meta.metaDescription && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Meta Description</p>
                  <p className="text-gray-700 dark:text-gray-300">{destination.meta.metaDescription}</p>
                </div>
              )}
              {destination.meta.metaKeywords && destination.meta.metaKeywords.length > 0 && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Meta Keywords</p>
                  <div className="flex flex-wrap gap-2">
                    {destination.meta.metaKeywords.map((keyword, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded text-sm">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {destination.meta.canonicalUrl && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Canonical URL</p>
                  <a
                    href={destination.meta.canonicalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                  >
                    {destination.meta.canonicalUrl}
                  </a>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">SEO Flags</p>
                  <div className="space-y-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Indexable: {destination.meta.indexable ? '✓ Yes' : '✗ No'}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Follow Links: {destination.meta.followLinks ? '✓ Yes' : '✗ No'}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      AI Discoverable: {destination.meta.aiDiscoverable ? '✓ Yes' : '✗ No'}
                    </p>
                    {destination.meta.language && (
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Language: {destination.meta.language}
                      </p>
                    )}
                  </div>
                </div>
                {destination.meta.structuredType && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Structured Type</p>
                    <p className="text-gray-900 dark:text-white">{destination.meta.structuredType}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="h-4 w-4 mr-2" />
              <span>Created: {formatDate(destination.createdOn)}</span>
            </div>
            {destination.updatedOn && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="h-4 w-4 mr-2" />
                <span>Updated: {formatDate(destination.updatedOn)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

















